import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackBannerClick } from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { ExternalLink } from 'lucide-react';
import { usePremiumStatus as useSharedPremiumStatus, useAdBanner } from '../lib/queries';

// Hook to check premium status — delegates to the shared React Query hook
// so that all AdBanner / AdPostBanner / WalkAdBanner instances on screen
// share a single HTTP request (previously each instance fired its own).
function usePremiumStatus() {
  const { data, isFetched } = useSharedPremiumStatus();
  if (!isFetched) return null;
  return data?.is_premium || false;
}

// Banner component for different positions
export function AdBanner({ position, className = '', prefetchedBanner = null, isPremiumKnown = false }) {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const isPremiumSelf = usePremiumStatus();
  // Fetch banner via shared React Query hook (dedup'd across all banner instances
  // with the same position, e.g. multiple home banners on one screen).
  const { data: fetchedBanner } = useAdBanner(position, { enabled: !prefetchedBanner && isPremiumSelf === false });
  const banner = prefetchedBanner || fetchedBanner;

  const handleClick = () => {
    if (!banner) return;

    // Internal Premium link takes precedence over the external URL.
    if (banner.link_to_premium) {
      navigate('/app/premium');
    } else if (banner.link_url) {
      window.open(banner.link_url, '_blank', 'noopener,noreferrer');
    }

    trackBannerClick(banner.banner_id).catch(() => {});
  };

  // Premium users NEVER see ads - check FIRST regardless of prefetched banner
  if (isPremiumSelf === true) return null;
  
  // Still loading premium status
  if (isPremiumSelf === null) return null;
  
  // Not premium but no banner loaded
  if (!banner) return null;

  return (
    <div 
      className={`relative rounded-xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform ${className}`}
      onClick={handleClick}
      data-testid={`ad-banner-${position}`}
    >
      <img 
        src={banner.image_url} 
        alt={banner.title}
        className="w-full h-auto object-cover"
      />
      
      {/* Link indicator if has link */}
      {banner.link_url && (
        <div className={`absolute bottom-2 right-2 w-6 h-6 rounded-full flex items-center justify-center ${
          isDark ? 'bg-white/20' : 'bg-black/20'
        }`}>
          <ExternalLink className="w-3 h-3 text-white" />
        </div>
      )}
    </div>
  );
}

// Full-width banner for feed (looks like a post)
export function AdPostBanner({ className = '', isFirstBanner = false, onShow, prefetchedBanner = null }) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isPremium = usePremiumStatus();
  // Shared React Query fetch — one request shared by every AdPostBanner on the feed,
  // no matter how many are rendered.
  const { data: fetchedBanner } = useAdBanner('feed', { enabled: !prefetchedBanner && isPremium === false });
  const banner = prefetchedBanner || fetchedBanner;

  const handleClick = () => {
    if (!banner) return;

    if (banner.link_to_premium) {
      navigate('/app/premium');
    } else if (banner.link_url) {
      window.open(banner.link_url, '_blank', 'noopener,noreferrer');
    }

    // Track click in background
    trackBannerClick(banner.banner_id).catch(() => {});
  };

  // Premium users NEVER see ads - check this FIRST regardless of prefetched banner
  if (isPremium === true) return null;
  
  // Still loading premium status
  if (isPremium === null) return null;
  
  // Not premium but no banner loaded
  if (!banner) return null;

  // Visibility flags from admin — default to TRUE so legacy banners
  // (without these fields) keep showing both title and sponsored label.
  const showTitle = banner.show_title !== false;
  const showSponsored = banner.show_sponsored !== false;
  const showHeader = showTitle || showSponsored;

  return (
    <div 
      className={`glass-card overflow-hidden cursor-pointer active:scale-[0.98] transition-transform ${className}`}
      onClick={handleClick}
      data-testid="ad-post-banner"
    >
      {/* Header like a post — hidden entirely if admin disabled BOTH
          title and sponsored label (keeps the card flush against the
          image, matching the walk overlay popup style). */}
      {showHeader && (
        <div className="p-4 pb-2 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isDark ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-amber-400 to-orange-500'
          }`}>
            <span className="text-white text-lg">📢</span>
          </div>
          <div className="flex-1">
            {showTitle && (
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {banner.title}
              </p>
            )}
            {showSponsored && (
              <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                {t('ads.sponsored')}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Image */}
      <div className="px-4 pb-4">
        <img 
          src={banner.image_url} 
          alt={banner.title}
          className="w-full rounded-xl"
        />
      </div>
      
      {/* CTA — shown when there's any actionable destination */}
      {(banner.link_url || banner.link_to_premium) && (
        <div className={`px-4 pb-4`}>
          <div className={`flex items-center justify-center gap-2 py-2 rounded-xl ${
            isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
          }`}>
            <span className="text-sm font-medium">{t('ads.learnMore')}</span>
            <ExternalLink className="w-4 h-4" />
          </div>
        </div>
      )}
    </div>
  );
}

// Timed banner overlay for walk tracking
export function WalkAdBanner({ duration = 5, onClose }) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(duration);
  const [canClose, setCanClose] = useState(false);
  const isPremium = usePremiumStatus();
  // Shared React Query hook — same cache key as any other "walk" banner.
  const { data: fetchedBanner, isFetched: bannerFetched } = useAdBanner('walk', { enabled: isPremium === false });
  const banner = (fetchedBanner && fetchedBanner.image_url) ? fetchedBanner : null;
  const loading = isPremium === null || !bannerFetched;

  useEffect(() => {
    // Premium users skip ads entirely
    if (isPremium === true && onClose) onClose();
  }, [isPremium, onClose]);

  // Start countdown as soon as component mounts (regardless of banner loading)
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanClose(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Auto-close if no banner after loading
  useEffect(() => {
    if (!loading && !banner && onClose) {
      onClose();
    }
  }, [loading, banner, onClose]);

  const handleClick = () => {
    if (!banner) return;

    if (banner.link_to_premium) {
      // Close overlay first so the user doesn't land back on the ad.
      if (onClose) onClose();
      navigate('/app/premium');
    } else if (banner.link_url) {
      window.open(banner.link_url, '_blank', 'noopener,noreferrer');
    }

    // Track click in background
    trackBannerClick(banner.banner_id).catch(() => {});
  };

  // Hide while checking premium status
  if (isPremium === null) return null;
  
  // Premium users don't see ads
  if (isPremium) return null;

  // If still loading, show loading spinner with countdown
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
        <div className={`max-w-sm w-full rounded-2xl overflow-hidden p-8 text-center ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}>
          <div className="spinner mx-auto mb-4"></div>
          <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
            {t('common.loading')} {countdown}s
          </p>
        </div>
      </div>
    );
  }

  // If no banner found, return null (will auto-close via useEffect)
  if (!banner) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className={`max-w-sm w-full rounded-2xl overflow-hidden ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}>
        {/* Banner content */}
        <div 
          className="cursor-pointer active:scale-[0.98] transition-transform"
          onClick={handleClick}
        >
          <img 
            src={banner.image_url} 
            alt={banner.title}
            className="w-full"
          />
          
          <div className="p-4">
            {banner.show_title !== false && (
              <h3 className={`font-display text-lg mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {banner.title}
              </h3>
            )}
            
            {(banner.link_url || banner.link_to_premium) && (
              <div className="flex items-center gap-2 text-[#E8334A]">
                <span className="text-sm font-medium">{t('ads.learnMore')}</span>
                <ExternalLink className="w-4 h-4" />
              </div>
            )}
          </div>
        </div>
        
        {/* Close button when countdown is 0 */}
        {canClose && (
          <div className="p-4 pt-0">
            <button 
              onClick={onClose}
              className={`w-full py-3 rounded-xl font-medium ${
                isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('common.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
